import { resolveConfig } from './config'
import type { ResolvedConfig } from './config'
import { buildBrowserContext } from './context'
import { IdentityManager } from './identity'
import { createLogger } from './logger'
import type { Logger } from './logger'
import { EventQueue } from './queue'
import { HttpTransport } from './transport'
import type { AnalyticsConfig, AnalyticsClientInterface, QueuedEvent, UserIdentity } from './types'
import { isoTimestamp } from './utils'

export class AnalyticsClient implements AnalyticsClientInterface {
  private config: ResolvedConfig
  private logger: Logger
  private identity: IdentityManager
  private transport: HttpTransport
  private queue: EventQueue

  constructor(config: AnalyticsConfig) {
    this.config = resolveConfig(config)
    this.logger = createLogger(this.config.debug)
    this.identity = new IdentityManager()

    this.transport = new HttpTransport({
      endpoint: this.config.endpoint,
      apiKey: this.config.apiKey,
      maxRetries: this.config.maxRetries,
      logger: this.logger,
      onError: this.config.onError,
    })

    this.queue = new EventQueue({
      flushAt: this.config.flushAt,
      flushInterval: this.config.flushInterval,
      transport: this.transport,
      logger: this.logger,
      beforeSend: this.config.beforeSend,
    })

    this.logger.debug('Initialized', { endpoint: this.config.endpoint })
  }

  track(name: string, props?: Record<string, unknown>): void {
    if (this.config.disabled) {
      return
    }

    const event: QueuedEvent = {
      type: 'track',
      ts: isoTimestamp(),
      name,
      anon_id: this.identity.getAnonId(),
      user_id: this.identity.getUserId(),
      session_id: this.identity.getSessionId(),
      props: { ...this.identity.getSuperProperties(), ...props },
      context: buildBrowserContext(),
    }

    this.queue.add(event)
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (this.config.disabled) {
      return
    }

    this.identity.identify(userId, traits)

    const event: QueuedEvent = {
      type: 'identify',
      ts: isoTimestamp(),
      anon_id: this.identity.getAnonId(),
      user_id: userId,
      session_id: this.identity.getSessionId(),
      props: traits,
      context: buildBrowserContext(),
    }

    this.queue.add(event)
  }

  setUser(identity: UserIdentity): void {
    if (this.config.disabled) {
      return
    }

    this.identity.setUser(identity)

    const event: QueuedEvent = {
      type: 'identify',
      ts: isoTimestamp(),
      anon_id: this.identity.getAnonId(),
      user_id: identity.userId,
      session_id: this.identity.getSessionId(),
      props: identity.traits,
      context: buildBrowserContext(),
    }

    this.queue.add(event)
  }

  resetUser(): void {
    if (this.config.disabled) {
      return
    }

    this.identity.reset()

    const event: QueuedEvent = {
      type: 'identify',
      ts: isoTimestamp(),
      anon_id: this.identity.getAnonId(),
      session_id: this.identity.getSessionId(),
      context: buildBrowserContext(),
    }

    this.queue.add(event)
  }

  setSuperProperties(props: Record<string, unknown>): void {
    this.identity.setSuperProperties(props)
  }

  async flush(timeout?: number): Promise<void> {
    if (timeout !== undefined) {
      await Promise.race([this.queue.flush(), new Promise((resolve) => setTimeout(resolve, timeout))])
    } else {
      await this.queue.flush()
    }
  }

  async close(timeout?: number): Promise<void> {
    this.logger.debug('Closing client')
    await this.queue.close(timeout)
  }
}

// === Module-level singleton + pending queue ===

let client: AnalyticsClient | null = null
const pendingQueue: Array<() => void> = []

export function init(config: AnalyticsConfig): AnalyticsClient {
  client = new AnalyticsClient(config)
  for (const fn of pendingQueue) {
    fn()
  }
  pendingQueue.length = 0
  return client
}

export function track(name: string, props?: Record<string, unknown>): void {
  if (client) {
    client.track(name, props)
  } else {
    pendingQueue.push(() => client!.track(name, props))
  }
}

export function identify(userId: string, traits?: Record<string, unknown>): void {
  if (client) {
    client.identify(userId, traits)
  } else {
    pendingQueue.push(() => client!.identify(userId, traits))
  }
}

export function setUser(identity: UserIdentity): void {
  if (client) {
    client.setUser(identity)
  } else {
    pendingQueue.push(() => client!.setUser(identity))
  }
}

export function resetUser(): void {
  if (client) {
    client.resetUser()
  } else {
    pendingQueue.push(() => client!.resetUser())
  }
}

export function setSuperProperties(props: Record<string, unknown>): void {
  if (client) {
    client.setSuperProperties(props)
  } else {
    pendingQueue.push(() => client!.setSuperProperties(props))
  }
}

export async function flush(timeout?: number): Promise<void> {
  if (client) {
    await client.flush(timeout)
  }
}

export async function close(timeout?: number): Promise<void> {
  if (client) {
    await client.close(timeout)
    client = null
  }
}
